import { useEffect, useState } from "react";
import {
  Card, Row, Col, Statistic, Button, Tag, List, Spin, Typography, Space, Divider
} from "antd";
import {
  CalendarOutlined, BankOutlined, TeamOutlined, BookOutlined,
  ScheduleOutlined, SyncOutlined, EyeOutlined, PlusOutlined,
  HomeOutlined, CheckCircleOutlined, ClockCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { selector } from "../../api";
import { appCRUD } from "../../actions/app";

const { Title, Text } = Typography;

const toTitleCase = (str) =>
  str ? str.replace(/\b\w/g, (c) => c.toUpperCase()) : "";

export const HomeContent = () => {
  const user = selector.User();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    campuses: 0, rooms: 0, programmes: 0,
    enrollments: 0, timetables: 0, examTimetables: 0,
  });
  const [activeAcademic, setActiveAcademic] = useState(null);
  const [activeSemester, setActiveSemester] = useState(null);
  const [recentTimetables, setRecentTimetables] = useState([]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [academicResp, campusResp, roomResp, progResp, enrollResp, ttResp] =
        await Promise.all([
          appCRUD.loadAcademicActive().catch(() => ({ data: [] })),
          appCRUD.loadCampas(1, 0, "", "").catch(() => ({ data: { count: 0 } })),
          appCRUD.loadRoom(1, 0, "", "").catch(() => ({ data: { count: 0 } })),
          appCRUD.loadProgramme(1, 0, "", "").catch(() => ({ data: { count: 0 } })),
          appCRUD.loadEnrollment(1, 0, "", "").catch(() => ({ data: { count: 0 } })),
          appCRUD.loadTimeTable(5, 0, "", "-id").catch(() => ({ data: { count: 0, results: [] } })),
        ]);

      const academics = academicResp.data || [];
      if (academics.length > 0) {
        setActiveAcademic(academics[0]);
        try {
          const semResp = await appCRUD.loadSemisterActive(academics[0].id);
          if (semResp.data?.length > 0) {
            setActiveSemester(semResp.data[0]);
          }
        } catch {}
      }

      setStats({
        campuses: campusResp.data?.count || 0,
        rooms: roomResp.data?.count || 0,
        programmes: progResp.data?.count || 0,
        enrollments: enrollResp.data?.count || 0,
        timetables: ttResp.data?.count || 0,
        examTimetables: 0,
      });

      setRecentTimetables(ttResp.data?.results || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboardData(); }, []);

  const statCards = [
    { title: "Campuses", value: stats.campuses, icon: <BankOutlined />, color: "#25bb57", path: "/campas" },
    { title: "Venues / Rooms", value: stats.rooms, icon: <HomeOutlined />, color: "#2596be", path: "/room" },
    { title: "Programmes", value: stats.programmes, icon: <BookOutlined />, color: "#722ed1", path: "/programme" },
    { title: "Allocations", value: stats.enrollments, icon: <TeamOutlined />, color: "#fa8c16", path: "/enrollment" },
    { title: "Timetables", value: stats.timetables, icon: <CalendarOutlined />, color: "#1890ff", path: "/timetable" },
    { title: "Exam Timetables", value: stats.examTimetables, icon: <ScheduleOutlined />, color: "#eb2f96", path: "/exam-timetable" },
  ];

  const quickActions = [
    { label: "Generate Timetable", icon: <PlusOutlined />, path: "/timetable", color: "#25bb57" },
    { label: "View Public Ratiba", icon: <EyeOutlined />, path: "/ratiba", color: "#2596be" },
    { label: "Manage Allocations", icon: <TeamOutlined />, path: "/enrollment", color: "#722ed1" },
    { label: "Room Management", icon: <HomeOutlined />, path: "/room", color: "#fa8c16" },
  ];

  const getStatusTag = (item) => {
    if (item.is_published) return <Tag color="success" icon={<CheckCircleOutlined />}>Published</Tag>;
    if (item.is_generated) return <Tag color="processing" icon={<ClockCircleOutlined />}>Generated</Tag>;
    return <Tag color="default">Draft</Tag>;
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: "linear-gradient(135deg, #25bb57 0%, #2596be 100%)",
        padding: "28px 32px", borderRadius: 14, marginBottom: 20, color: "#fff",
      }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Title level={3} style={{ color: "#fff", margin: 0 }}>
              Welcome back, {toTitleCase(user?.first_name || user?.username || "Admin")}
            </Title>
            <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, display: "block", marginTop: 4 }}>
              {activeAcademic ? `Academic Year: ${activeAcademic.name}` : "No active academic year"}
              {activeSemester && (
                <Tag style={{ marginLeft: 12, color: "#fff", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}>
                  {toTitleCase(activeSemester.name)}
                </Tag>
              )}
            </Text>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: "right" }}>
            <Space>
              <Tag color="gold" style={{ fontSize: 13, padding: "4px 12px" }}>
                {user?.role || "Administrator"}
              </Tag>
              <Button icon={<SyncOutlined />} onClick={loadDashboardData}
                ghost style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Stat Cards */}
      <Row gutter={[16, 16]}>
        {statCards.map((card, i) => (
          <Col xs={12} sm={8} md={4} key={i}>
            <Card hoverable onClick={() => navigate(card.path)}
              style={{ borderRadius: 12, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", textAlign: "center", cursor: "pointer", transition: "transform 0.2s" }}
              bodyStyle={{ padding: "20px 12px" }}>
              <div style={{ color: card.color, fontSize: 28, marginBottom: 8 }}>{card.icon}</div>
              <Statistic title={<span style={{ fontSize: 12 }}>{card.title}</span>}
                value={card.value} valueStyle={{ color: card.color, fontSize: 28, fontWeight: 700 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Quick Actions + Recent Timetables */}
      <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
        <Col xs={24} lg={10}>
          <Card title={<span><ScheduleOutlined style={{ marginRight: 8 }} />Quick Actions</span>}
            style={{ borderRadius: 12, height: "100%" }}>
            {quickActions.map((action, i) => (
              <Button key={i} icon={action.icon} size="large" block
                onClick={() => navigate(action.path)}
                style={{ marginBottom: 12, textAlign: "left", height: 50, borderRadius: 10, borderColor: action.color, color: action.color }}>
                {action.label}
                <RightOutlined style={{ float: "right", marginTop: 4 }} />
              </Button>
            ))}
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title={<span><CalendarOutlined style={{ marginRight: 8 }} />Recent Timetables</span>}
            style={{ borderRadius: 12, height: "100%" }}>
            {recentTimetables.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                <CalendarOutlined style={{ fontSize: 40, marginBottom: 12 }} />
                <p>No timetables created yet</p>
                <Button type="primary" onClick={() => navigate("/timetable")}>Create First Timetable</Button>
              </div>
            ) : (
              <List dataSource={recentTimetables} renderItem={(item) => (
                <List.Item style={{ cursor: "pointer", padding: "14px 0" }}
                  actions={[getStatusTag(item)]} onClick={() => navigate("/timetable")}>
                  <List.Item.Meta
                    avatar={
                      <div style={{
                        width: 44, height: 44, borderRadius: 10,
                        background: "linear-gradient(135deg, #25bb57, #2596be)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontWeight: 700, fontSize: 16,
                      }}>
                        {toTitleCase(item.cumpas?.name || "?").charAt(0)}
                      </div>
                    }
                    title={<Text strong>{toTitleCase(item.cumpas?.name || "Unknown")} Campus</Text>}
                    description={
                      <Space size={4}>
                        <Text type="secondary">{item.academic?.name}</Text>
                        <Divider type="vertical" />
                        <Text type="secondary">{toTitleCase(item.semister?.name || "")}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Academic Year Info */}
      {activeAcademic && (
        <Card style={{ marginTop: 20, borderRadius: 12 }} size="small">
          <Row gutter={[24, 8]}>
            <Col xs={24} sm={8}>
              <Text type="secondary">Academic Year</Text>
              <Title level={5} style={{ margin: "4px 0 0" }}>{activeAcademic.name}</Title>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Start Date</Text>
              <Title level={5} style={{ margin: "4px 0 0" }}>{activeAcademic.start_date || "N/A"}</Title>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">End Date</Text>
              <Title level={5} style={{ margin: "4px 0 0" }}>{activeAcademic.end_date || "N/A"}</Title>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};
